import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import GameScreen from "./components/game-screen/GameScreen";
import LevelScreen from "./components/level-screen/LevelScreen";
import GameOver from "./components/game-over/GameOver";
import { easyWords, mediumWords, hardWords, expertWords } from "./config/words";
import { easyWordsTR, mediumWordsTR, hardWordsTR, expertWordsTR } from "./config/wordsTR";
import { play as playSound, preload as preloadSounds } from "./utils/audio";
import { getLanguage, subscribeLanguage, isLetter, normalizeLetter } from "./utils/language";

const POOLS = {
  en: { easy: easyWords, medium: mediumWords, hard: hardWords, expert: expertWords },
  tr: { easy: easyWordsTR, medium: mediumWordsTR, hard: hardWordsTR, expert: expertWordsTR },
};

const LEVEL_PARAMS = {
  easy:   { fallSpeed: 1.4, spawnInterval: 2400, initialSpawn: 2, maxActive: 4, damage: 1 },
  medium: { fallSpeed: 1.8, spawnInterval: 2000, initialSpawn: 2, maxActive: 5, damage: 1 },
  hard:   { fallSpeed: 2.0, spawnInterval: 1800, initialSpawn: 2, maxActive: 5, damage: 1 },
  expert: { fallSpeed: 2.4, spawnInterval: 1600, initialSpawn: 3, maxActive: 6, damage: 1 },
};

const getLevelConfig = (level, lang) => {
  const params = LEVEL_PARAMS[level];
  const pool = POOLS[lang]?.[level] || POOLS.en[level];
  return { ...params, pool };
};

const TICK_MS = 16;
const MAX_HEALTH = 3;
const SPAWN_Y = -12;
const BOTTOM_LIMIT = 84;
const LEFT_MIN = 4;
const LEFT_MAX = 55;

const WAVE_SIZE = 10;
const SPEEDUP_INTERVAL_MS = 20000;
const SPEEDUP_FACTOR = 1.04;
const SPAWN_SPEEDUP_FACTOR = 0.96;
const SPEED_CAP = 1.8;
const SPAWN_FLOOR = 0.6;

const POWERUP_CHANCE = 0.18;
const DANGER_CHANCE = 0.08;
const POWERUP_KINDS = ["slow", "freeze", "heart", "bombclear"];

const SLOW_DURATION_MS = 5000;
const SLOW_FACTOR = 0.4;
const FREEZE_DURATION_MS = 2000;

const LONG_WORD_THRESHOLD = 8;
const LONG_WORD_BONUS = 5;
const PERFECT_BONUS = 5;
const DANGER_MULTIPLIER = 2;

const comboMultiplier = (combo) => {
  if (combo >= 20) return 4;
  if (combo >= 10) return 3;
  if (combo >= 5) return 2;
  return 1;
};

const randomLeft = (active) => {
  for (let attempt = 0; attempt < 6; attempt++) {
    const x = LEFT_MIN + Math.random() * (LEFT_MAX - LEFT_MIN);
    const tooClose = active.some((w) => w.y < 15 && Math.abs(w.x - x) < 18);
    if (!tooClose) return x;
  }
  return LEFT_MIN + Math.random() * (LEFT_MAX - LEFT_MIN);
};

const pickWord = (pool, active) => {
  const taken = new Set(active.map((w) => w.text));
  const usableFirstLetters = new Set(active.map((w) => w.text[0]));
  const available = pool.filter(
    (w) => !taken.has(w) && !usableFirstLetters.has(w[0])
  );
  const fromList = available.length > 0 ? available : pool.filter((w) => !taken.has(w));
  if (fromList.length === 0) return null;
  return fromList[Math.floor(Math.random() * fromList.length)];
};

const rollWordKind = () => {
  const r = Math.random();
  if (r < DANGER_CHANCE) return "danger";
  if (r < DANGER_CHANCE + POWERUP_CHANCE) {
    return POWERUP_KINDS[Math.floor(Math.random() * POWERUP_KINDS.length)];
  }
  return "normal";
};

let floatingId = 0;
const nextFloatingId = () => ++floatingId;

const App = () => {
  const [currentState, setCurrentState] = useState("level");
  const [level, setLevel] = useState("");
  const [activeWords, setActiveWords] = useState([]);
  const [typingWord, setTypingWord] = useState("");
  const [matchedWordId, setMatchedWordId] = useState(null);
  const [score, setScore] = useState(0);
  const [health, setHealth] = useState(MAX_HEALTH);
  const [errorPulse, setErrorPulse] = useState(0);

  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [wave, setWave] = useState(1);
  const [wordsCompletedThisWave, setWordsCompletedThisWave] = useState(0);
  const [waveBanner, setWaveBanner] = useState(null);
  const [floatingTexts, setFloatingTexts] = useState([]);
  const [particles, setParticles] = useState([]);
  const [healthFlash, setHealthFlash] = useState(0);
  const [slowActive, setSlowActive] = useState(false);
  const [freezeActive, setFreezeActive] = useState(false);
  const [bombPulse, setBombPulse] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;

  const [lang, setLang] = useState(getLanguage());
  const langRef = useRef(lang);
  langRef.current = lang;

  useEffect(() => subscribeLanguage(setLang), []);
  const [hadErrorOnCurrent, setHadErrorOnCurrent] = useState(false);
  const [stats, setStats] = useState({ correctChars: 0, totalChars: 0, wordsCompleted: 0, elapsedMs: 0 });
  const [newRecord, setNewRecord] = useState(false);

  const startTimeRef = useRef(0);
  const pausedAtRef = useRef(0);
  const pausedTotalRef = useRef(0);
  const correctCharsRef = useRef(0);
  const totalCharsRef = useRef(0);
  const wordsCompletedRef = useRef(0);
  const particleIdRef = useRef(0);
  const speedFactorRef = useRef(1);
  const spawnFactorRef = useRef(1);
  const slowUntilRef = useRef(0);
  const freezeUntilRef = useRef(0);
  const wavePausedRef = useRef(false);

  const errorTimeoutRef = useRef(null);
  const idRef = useRef(0);
  const activeWordsRef = useRef(activeWords);
  const matchedIdRef = useRef(matchedWordId);
  const typingRef = useRef(typingWord);
  const stateRef = useRef(currentState);
  const levelRef = useRef(level);
  const hadErrorRef = useRef(false);
  const comboRef = useRef(0);

  activeWordsRef.current = activeWords;
  matchedIdRef.current = matchedWordId;
  typingRef.current = typingWord;
  stateRef.current = currentState;
  levelRef.current = level;
  hadErrorRef.current = hadErrorOnCurrent;
  comboRef.current = combo;

  const resetRunState = useCallback(() => {
    setActiveWords([]);
    setTypingWord("");
    setMatchedWordId(null);
    setScore(0);
    setHealth(MAX_HEALTH);
    setCombo(0);
    setBestCombo(0);
    setWave(1);
    setWordsCompletedThisWave(0);
    setWaveBanner(null);
    setFloatingTexts([]);
    setParticles([]);
    setHealthFlash(0);
    setSlowActive(false);
    setFreezeActive(false);
    setBombPulse(0);
    setPaused(false);
    setHadErrorOnCurrent(false);
    setStats({ correctChars: 0, totalChars: 0, wordsCompleted: 0, elapsedMs: 0 });
    setNewRecord(false);
    startTimeRef.current = performance.now();
    pausedAtRef.current = 0;
    pausedTotalRef.current = 0;
    correctCharsRef.current = 0;
    totalCharsRef.current = 0;
    wordsCompletedRef.current = 0;
    speedFactorRef.current = 1;
    spawnFactorRef.current = 1;
    slowUntilRef.current = 0;
    freezeUntilRef.current = 0;
    wavePausedRef.current = false;
  }, []);

  const startGame = useCallback((chosenLevel) => {
    resetRunState();
    setLevel(chosenLevel);
    setCurrentState("game");
  }, [resetRunState]);

  const backToLevel = useCallback(() => {
    setLevel("");
    resetRunState();
    setCurrentState("level");
  }, [resetRunState]);

  const addFloating = useCallback((text, x, y, variant) => {
    const id = nextFloatingId();
    setFloatingTexts((prev) => [...prev, { id, text, x, y, variant }]);
    setTimeout(() => {
      setFloatingTexts((prev) => prev.filter((f) => f.id !== id));
    }, 900);
  }, []);

  const burstParticles = useCallback((x, y, color) => {
    const id = particleIdRef.current++;
    const pieces = Array.from({ length: 14 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 14 + Math.random() * 0.4;
      const dist = 40 + Math.random() * 60;
      return {
        id: `${id}-${i}`,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        rot: Math.random() * 360,
      };
    });
    const burst = { id, x, y, color, pieces };
    setParticles((prev) => [...prev, burst]);
    setTimeout(() => {
      setParticles((prev) => prev.filter((p) => p.id !== id));
    }, 700);
  }, []);

  const flashHealth = useCallback(() => {
    setHealthFlash((n) => n + 1);
  }, []);

  useEffect(() => {
    if (currentState !== "game" || !level) return;
    const cfg = getLevelConfig(level, langRef.current);

    let lastFrozen = false;
    let lastSlow = false;
    const tick = setInterval(() => {
      if (wavePausedRef.current || pausedRef.current) return;
      const now = performance.now();
      const isFrozen = now < freezeUntilRef.current;
      if (isFrozen !== lastFrozen) {
        lastFrozen = isFrozen;
        setFreezeActive(isFrozen);
      }
      if (isFrozen) return;
      const isSlow = now < slowUntilRef.current;
      if (isSlow !== lastSlow) {
        lastSlow = isSlow;
        setSlowActive(isSlow);
      }
      const slowMul = isSlow ? SLOW_FACTOR : 1;

      const prev = activeWordsRef.current;
      const next = [];
      let damageTaken = 0;
      let comboBroken = false;
      for (const w of prev) {
        const speed = cfg.fallSpeed * speedFactorRef.current * slowMul;
        const ny = w.y + speed * (TICK_MS / 100);
        if (ny >= BOTTOM_LIMIT) {
          if (w.kind === "danger") damageTaken += cfg.damage * 2;
          else if (w.kind === "normal") damageTaken += cfg.damage;
          comboBroken = true;
          continue;
        }
        next.push({ ...w, y: ny });
      }
      setActiveWords(next);
      if (damageTaken > 0) {
        setHealth((h) => Math.max(0, h - damageTaken));
        flashHealth();
      }
      if (comboBroken) {
        setCombo(0);
      }
      if (matchedIdRef.current != null && !next.some((w) => w.id === matchedIdRef.current)) {
        setMatchedWordId(null);
        setTypingWord("");
        setHadErrorOnCurrent(false);
      }
    }, TICK_MS);

    const spawnOne = (prev) => {
      if (prev.length >= cfg.maxActive) return prev;
      const text = pickWord(cfg.pool, prev);
      if (!text) return prev;
      idRef.current += 1;
      const kind = rollWordKind();
      return [...prev, { id: idRef.current, text, kind, x: randomLeft(prev), y: SPAWN_Y }];
    };

    let staggered = activeWordsRef.current;
    for (let i = 0; i < cfg.initialSpawn; i++) {
      staggered = spawnOne(staggered);
      const last = staggered[staggered.length - 1];
      if (last) last.y = SPAWN_Y - i * 14;
    }
    setActiveWords(staggered);

    let spawner;
    const scheduleSpawner = () => {
      const interval = Math.max(SPAWN_FLOOR * cfg.spawnInterval, cfg.spawnInterval * spawnFactorRef.current);
      spawner = setInterval(() => {
        if (wavePausedRef.current || pausedRef.current) return;
        setActiveWords((prev) => spawnOne(prev));
      }, interval);
    };
    scheduleSpawner();

    const speedup = setInterval(() => {
      if (wavePausedRef.current || pausedRef.current) return;
      speedFactorRef.current = Math.min(SPEED_CAP, speedFactorRef.current * SPEEDUP_FACTOR);
      spawnFactorRef.current = Math.max(SPAWN_FLOOR, spawnFactorRef.current * SPAWN_SPEEDUP_FACTOR);
      clearInterval(spawner);
      scheduleSpawner();
    }, SPEEDUP_INTERVAL_MS);

    const statsTick = setInterval(() => {
      if (pausedRef.current) return;
      const elapsedMs = performance.now() - startTimeRef.current - pausedTotalRef.current;
      setStats({
        correctChars: correctCharsRef.current,
        totalChars: totalCharsRef.current,
        wordsCompleted: wordsCompletedRef.current,
        elapsedMs,
      });
    }, 250);

    return () => {
      clearInterval(tick);
      clearInterval(spawner);
      clearInterval(speedup);
      clearInterval(statsTick);
    };
  }, [currentState, level, lang, flashHealth]);

  useEffect(() => {
    preloadSounds();
  }, []);

  useEffect(() => {
    if (health <= 0 && currentState === "game") {
      const key = `writé:highscore:${levelRef.current}`;
      const prev = parseInt(localStorage.getItem(key) || "0", 10);
      if (score > prev) {
        localStorage.setItem(key, String(score));
        setNewRecord(true);
      } else {
        setNewRecord(false);
      }
      playSound("gameover");
      setCurrentState("gameover");
    }
  }, [health, currentState, score]);

  useEffect(() => {
    if (wordsCompletedThisWave < WAVE_SIZE) return;
    if (currentState !== "game") return;
    const cfg = getLevelConfig(levelRef.current, langRef.current);
    if (!cfg || !cfg.pool) return;
    const newWave = wave + 1;
    setWordsCompletedThisWave(0);
    setWave(newWave);
    setWaveBanner({ id: Date.now(), text: `WAVE ${newWave}` });
    playSound("wave");
    wavePausedRef.current = true;
    setActiveWords([]);
    setMatchedWordId(null);
    setTypingWord("");
    setHadErrorOnCurrent(false);
    setTimeout(() => {
      if (stateRef.current !== "game") return;
      wavePausedRef.current = false;
      setWaveBanner(null);
      setActiveWords((prev) => {
        let next = prev;
        for (let i = 0; i < cfg.initialSpawn; i++) {
          if (next.length >= cfg.maxActive) break;
          const text = pickWord(cfg.pool, next);
          if (!text) break;
          idRef.current += 1;
          next = [
            ...next,
            {
              id: idRef.current,
              text,
              kind: rollWordKind(),
              x: randomLeft(next),
              y: SPAWN_Y - i * 14,
            },
          ];
        }
        return next;
      });
    }, 1800);
  }, [wordsCompletedThisWave, wave, currentState]);

  useEffect(() => {
    const onKey = (e) => {
      if (stateRef.current !== "game") return;
      if (wavePausedRef.current) return;

      const togglePause = () => {
        setPaused((p) => {
          const next = !p;
          if (next) {
            pausedAtRef.current = performance.now();
          } else if (pausedAtRef.current) {
            pausedTotalRef.current += performance.now() - pausedAtRef.current;
            pausedAtRef.current = 0;
          }
          return next;
        });
      };

      if (e.key === "Tab") {
        e.preventDefault();
        togglePause();
        return;
      }

      if (pausedRef.current) {
        if (e.key === "Escape") togglePause();
        return;
      }

      if (e.key === "Escape") {
        if (typingRef.current === "" && matchedIdRef.current == null) {
          togglePause();
        } else {
          setTypingWord("");
          setMatchedWordId(null);
          setHadErrorOnCurrent(false);
        }
        return;
      }

      if (e.key === "Backspace") {
        const t = typingRef.current;
        if (t.length <= 1) {
          setTypingWord("");
          setMatchedWordId(null);
          setHadErrorOnCurrent(false);
        } else {
          setTypingWord(t.slice(0, -1));
        }
        return;
      }

      if (e.key.length !== 1 || !isLetter(e.key)) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const ch = normalizeLetter(e.key);
      const active = activeWordsRef.current;
      let matchedId = matchedIdRef.current;
      let typed = typingRef.current;

      const triggerError = () => {
        setErrorPulse((n) => n + 1);
        setCombo(0);
        setHadErrorOnCurrent(true);
        totalCharsRef.current += 1;
        playSound("wrong");
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      };

      if (matchedId == null) {
        const candidates = active.filter((w) => w.text.startsWith(ch));
        if (candidates.length === 0) {
          if (active.length > 0) triggerError();
          return;
        }
        const lowest = candidates.reduce((a, b) => (a.y > b.y ? a : b));
        matchedId = lowest.id;
        typed = ch;
        setMatchedWordId(matchedId);
        setTypingWord(typed);
        setHadErrorOnCurrent(false);
        correctCharsRef.current += 1;
        totalCharsRef.current += 1;
        playSound("type");
      } else {
        const current = active.find((w) => w.id === matchedId);
        if (!current) {
          setMatchedWordId(null);
          setTypingWord("");
          setHadErrorOnCurrent(false);
          return;
        }
        if (typed.length >= current.text.length) {
          triggerError();
          return;
        }
        const nextTyped = typed + ch;
        typed = nextTyped;
        setTypingWord(typed);
        if (!current.text.startsWith(nextTyped)) {
          triggerError();
          return;
        }
        correctCharsRef.current += 1;
        totalCharsRef.current += 1;
        playSound("type");
      }

      const target = active.find((w) => w.id === matchedId);
      if (target && typed === target.text) {
        const hadError = hadErrorRef.current;
        const base = target.text.length;
        const mul = comboMultiplier(comboRef.current + 1);
        let gained = base * mul;
        let bonusLabel = "";

        if (target.kind === "danger") {
          gained *= DANGER_MULTIPLIER;
          bonusLabel = "DANGER x2";
        }
        if (target.text.length >= LONG_WORD_THRESHOLD) {
          gained += LONG_WORD_BONUS;
          bonusLabel = bonusLabel ? bonusLabel + " · LONG" : "LONG";
        }
        if (!hadError) {
          gained += PERFECT_BONUS;
          bonusLabel = bonusLabel ? bonusLabel + " · PERFECT" : "PERFECT";
        }

        setScore((s) => s + gained);
        addFloating(`+${gained}`, target.x, target.y, "score");
        if (bonusLabel) addFloating(bonusLabel, target.x, target.y - 5, "bonus");
        const burstColor =
          target.kind === "danger" ? "var(--danger)" :
          target.kind === "slow" ? "#60a5fa" :
          target.kind === "freeze" ? "#67e8f9" :
          target.kind === "heart" ? "#f472b6" :
          target.kind === "bombclear" ? "#fbbf24" :
          "var(--accent)";
        burstParticles(target.x, target.y, burstColor);
        wordsCompletedRef.current += 1;

        if (target.kind === "slow") {
          slowUntilRef.current = performance.now() + SLOW_DURATION_MS;
          addFloating("SLOW", target.x, target.y - 10, "powerup");
          playSound("slow");
        } else if (target.kind === "freeze") {
          freezeUntilRef.current = performance.now() + FREEZE_DURATION_MS;
          addFloating("FREEZE", target.x, target.y - 10, "powerup");
          playSound("freeze");
        } else if (target.kind === "heart") {
          setHealth((h) => Math.min(MAX_HEALTH, h + 1));
          addFloating("+HEART", target.x, target.y - 10, "powerup");
          playSound("heart");
        } else if (target.kind === "danger") {
          playSound("danger");
        } else if (target.kind === "normal") {
          playSound("complete");
        } else if (target.kind === "bombclear") {
          setBombPulse((n) => n + 1);
          playSound("bomb");
          const remaining = activeWordsRef.current.filter((w) => w.id !== matchedId);
          let bombScore = 0;
          for (const w of remaining) {
            bombScore += w.text.length;
            addFloating(`+${w.text.length}`, w.x, w.y, "score");
            burstParticles(w.x, w.y, "#fbbf24");
          }
          setScore((s) => s + bombScore);
          setActiveWords([]);
          setWordsCompletedThisWave((n) => n + remaining.length + 1);
          wordsCompletedRef.current += remaining.length;
          addFloating("CLEAR", target.x, target.y - 10, "powerup");
          setCombo((c) => {
            const nc = c + 1 + remaining.length;
            setBestCombo((bc) => Math.max(bc, nc));
            return nc;
          });
          setTypingWord("");
          setMatchedWordId(null);
          setHadErrorOnCurrent(false);
          return;
        }

        setActiveWords((prev) => prev.filter((w) => w.id !== matchedId));
        setCombo((c) => {
          const nc = c + 1;
          setBestCombo((bc) => Math.max(bc, nc));
          return nc;
        });
        setWordsCompletedThisWave((n) => n + 1);
        setTypingWord("");
        setMatchedWordId(null);
        setHadErrorOnCurrent(false);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [addFloating, burstParticles]);

  const minutes = stats.elapsedMs / 60000;
  const wpm = minutes > 0 ? Math.round(stats.correctChars / 5 / minutes) : 0;
  const accuracy = stats.totalChars > 0 ? Math.round((stats.correctChars / stats.totalChars) * 100) : 100;

  if (currentState === "level") {
    return <LevelScreen level={level} setLevel={startGame} />;
  }
  if (currentState === "gameover") {
    return (
      <GameOver
        score={score}
        level={level}
        wave={wave}
        bestCombo={bestCombo}
        wpm={wpm}
        accuracy={accuracy}
        newRecord={newRecord}
        onRestart={() => startGame(level)}
        onMenu={backToLevel}
      />
    );
  }
  return (
    <GameScreen
      activeWords={activeWords}
      typingWord={typingWord}
      matchedWordId={matchedWordId}
      health={health}
      score={score}
      combo={combo}
      multiplier={comboMultiplier(combo)}
      wave={wave}
      wpm={wpm}
      accuracy={accuracy}
      waveBanner={waveBanner}
      floatingTexts={floatingTexts}
      particles={particles}
      healthFlash={healthFlash}
      slowActive={slowActive}
      freezeActive={freezeActive}
      bombPulse={bombPulse}
      errorPulse={errorPulse}
      paused={paused}
      onRestart={backToLevel}
    />
  );
};

export default App;
